/**
 * Render a raster image deformed by a lattice as a triangle mesh of
 * affine-mapped `<image>` slices. Each lattice cell is split into two
 * triangles; for each triangle we solve a 2x3 affine that maps the
 * source pixel coordinates to the warped destination triangle, then
 * clip the image to that triangle.
 */
import type { Lattice } from "./latticeWarp";
import {
    appendAffineClippedImageUse,
    escapeSvgAttr,
    fmtSvgNum,
} from "./affineImageTriangle";

export interface LatticeRasterSource {
    dataUrl: string;
    /** Native (post-downscale) image width in pixels. */
    w: number;
    /** Native (post-downscale) image height in pixels. */
    h: number;
}

interface ViewBox {
    x: number;
    y: number;
    width: number;
    height: number;
}

/**
 * Build a self-contained SVG that renders `raster` warped by `lattice`.
 * Assumes `vb` already matches the raster's pixel dimensions (the
 * lattice context creates the source SVG with `viewBox="0 0 W H"`).
 */
export function buildRasterLatticeWarpSvg(
    raster: LatticeRasterSource,
    lattice: Lattice,
    vb: ViewBox,
): string {
    if (!lattice.length || !lattice[0].length) return "";
    const rows = lattice.length - 1;
    const cols = lattice[0].length - 1;
    if (rows < 1 || cols < 1) return "";

    const W = raster.w;
    const H = raster.h;
    const cellW = W / cols;
    const cellH = H / rows;

    const escDataUrl = escapeSvgAttr(raster.dataUrl);

    const defs: string[] = [
        `<image id="ldRasterImg" href="${escDataUrl}" xlink:href="${escDataUrl}" preserveAspectRatio="none" width="${fmtSvgNum(W)}" height="${fmtSvgNum(H)}" />`,
    ];
    const body: string[] = [];
    const clipIdxRef = { value: 0 };

    for (let j = 0; j < rows; j++) {
        for (let i = 0; i < cols; i++) {
            const d00 = lattice[j][i];
            const d10 = lattice[j][i + 1];
            const d11 = lattice[j + 1][i + 1];
            const d01 = lattice[j + 1][i];
            if (!d00 || !d10 || !d11 || !d01) continue;

            const u0 = i * cellW;
            const v0 = j * cellH;
            const u1 = (i + 1) * cellW;
            const v1 = (j + 1) * cellH;

            const tris: Array<{
                src: [number, number, number, number, number, number];
                dst: [number, number, number, number, number, number];
            }> = [
                {
                    src: [u0, v0, u1, v0, u1, v1],
                    dst: [d00.x, d00.y, d10.x, d10.y, d11.x, d11.y],
                },
                {
                    src: [u0, v0, u1, v1, u0, v1],
                    dst: [d00.x, d00.y, d11.x, d11.y, d01.x, d01.y],
                },
            ];

            for (const t of tris) {
                appendAffineClippedImageUse(
                    defs,
                    body,
                    "ldRasterImg",
                    t,
                    "ldClip",
                    clipIdxRef,
                );
            }
        }
    }

    return (
        `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" ` +
        `viewBox="${fmtSvgNum(vb.x)} ${fmtSvgNum(vb.y)} ${fmtSvgNum(vb.width)} ${fmtSvgNum(vb.height)}">` +
        `<defs>${defs.join("")}</defs>` +
        body.join("") +
        `</svg>`
    );
}
